/**
 * True for an absolute `http:` or `https:` URL.
 *
 * Used before any API-supplied or admin-entered address becomes an `href`, so a
 * `javascript:` or `data:` value can never be rendered as a clickable link.
 */
/**
 * The value as an in-app path to return to after sign-in, or null.
 *
 * It must be a single-slash absolute path, which rejects `//evil.example`,
 * `https://evil.example` and backslash tricks, so a crafted `returnTo` can never
 * become an open redirect. The sign-in route itself is refused so a return cannot
 * loop back to it.
 */
export function safeReturnPath(value: unknown): string | null {
  if (
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    value.length > 2048
  ) {
    return null;
  }

  const path = value.split(/[?#]/u, 1)[0] ?? '';

  return path === '/login' || path.startsWith('/login/') ? null : value;
}

export function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

const GATEWAY_PATH = /^\/g\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/$/u;

/**
 * True for a secure gateway entry point (ADR-40): `/g/<project id>/` on this
 * portal's own origin, with no query or fragment. The gateway cookie is set on
 * this origin with `Path=/g/`, so a gateway URL anywhere else could never work
 * and is refused rather than followed.
 */
export function isGatewayUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.origin === window.location.origin &&
      GATEWAY_PATH.test(url.pathname) &&
      url.search === '' &&
      url.hash === ''
    );
  } catch {
    return false;
  }
}
